import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  createEmployee,
  deleteEmployee,
  editEmployee,
  getEmployee,
  getEmployees,
  grantEmployeeAccess,
  resetEmployeePassword,
  revokeEmployeeAccess,
  updateEmployeeRoles,
  impersonateEmployee,
} from "api/employeeApi"
import {
  useCreateResource,
  useDeleteResource,
  useEditResource,
  usePaginatedResource,
  useResourceItem,
} from "./useCrudResource"

export const EMPLOYEE_QUERY_KEY = "employees"

export const useEmployees = (params = {}, enabled = true) =>
  usePaginatedResource(EMPLOYEE_QUERY_KEY, getEmployees, params, enabled)

export const useEmployee = (id, enabled = true) =>
  useResourceItem(EMPLOYEE_QUERY_KEY, getEmployee, id, enabled)

export const useCreateEmployee = () =>
  useCreateResource(createEmployee, EMPLOYEE_QUERY_KEY)

export const useEditEmployee = () =>
  useEditResource(editEmployee, EMPLOYEE_QUERY_KEY)

export const useDeleteEmployee = () =>
  useDeleteResource(deleteEmployee, EMPLOYEE_QUERY_KEY)

// System access mutations — invalidate the employee + list cache so System Access card refreshes

export const useGrantEmployeeAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: grantEmployeeAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY],
        refetchType: "active",
      })
    },
  })
}

export const useRevokeEmployeeAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: revokeEmployeeAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY],
        refetchType: "active",
      })
    },
  })
}

export const useUpdateEmployeeRoles = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateEmployeeRoles,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY],
        refetchType: "active",
      })
    },
  })
}

export const useResetEmployeePassword = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetEmployeePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY],
        refetchType: "active",
      })
    },
  })
}

export const useImpersonateEmployee = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: impersonateEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY],
        refetchType: 'active',
      })
    },
  })
}
